import { useEffect, useState } from 'react';
import { useUserStrategies } from '../../../context/UserStrategies';
import { GridWidget } from '../types';
import { filtersList, FilterType } from './constants';
import { ContributionLegend } from './ContributionLegend';
import ContributionWidgetFilters from './Filters';

import { Legend } from '../../Legend/Legend';
import { Tabs } from './Tabs';
import TreemapChart from './TreemapChart';
import {
  aggregateGroups,
  calcStrategyValues,
  computePercentage,
  getSymbol,
} from '../../../helpers/functions';

import { ProfitLoss } from '../../ProfitLoss';
import { t } from 'i18next';
import { logger } from '../../../helpers/logger';
import { IStrategy } from '../../../types/model';

const Contribution = ({
  strategyId,
  width,
  height,
  setLegend,
  isFixedHeight,
}: GridWidget & { strategyId?: string; isFixedHeight?: boolean }) => {
  useEffect(() => {
    setLegend?.(
      <Legend CustomLegend={ContributionLegend} collapsed={width === 1} />
    );
  }, [setLegend, width]);

  const { strategies } = useUserStrategies();

  // const TAB_LIMIT = 1;

  const [selectedFilter, setSelectedFilter] = useState<FilterType>('Asset');
  const [selectedStrategy, setSelectedStrategy] = useState<IStrategy>(
    strategyId ? strategies.find(({ id }) => id === strategyId)! : strategies[0]
  );

  const [selectedStrategies, setSelectedStrategies] = useState<string[]>(
    // Array.from(Array(TAB_LIMIT).keys()).map((index) => {
    //   return strategies[index] && index < 1 ? strategies[index].id : '';
    // })
    [strategies[0].id]
  );

  logger.log('Contribution:selectedStrategy', [selectedStrategy.id]);

  const handleStrategySelect = (selectedId: string, index: number) => {
    logger.log('Contribution:handleStrategySelect', selectedId, index);
    // const updatedSelectedStrategies = [...selectedStrategies];
    // updatedSelectedStrategies[index] = selectedId;
    // Array.from(Array(TAB_LIMIT).keys()).map((i) => {
    //   return strategies[index] && i === index ? strategies[index].id : '';
    // });

    setSelectedStrategy(strategies.find(({ id }) => id === selectedId)!);
    setSelectedStrategies([selectedId]);
  };

  const handleStrategyRemoval = (index: number) => {
    logger.log('Contribution:handleStrategyRemoval', index);

    // const updatedSelectedStrategies = [...selectedStrategies];
    // updatedSelectedStrategies[index] = '';
    // setSelectedStrategies(updatedSelectedStrategies);

    // const remainSelectedStrategyId = selectedStrategies.find((id) => id);
    // setSelectedStrategy(
    //   strategies.find(({ id }) => id === selectedStrategies[index])!
    // );
  };

  const strategyValues = calcStrategyValues(selectedStrategy);

  logger.log(strategyValues);

  const chartData: {
    currency?: string;
    name: string;
    value: number;
    percentage?: number;
    treeMapValue: number;
    tooltipName: string;
  }[] = (
    selectedFilter === 'Asset'
      ? selectedStrategy.strategyAssets.items.map(
          ({
            asset: {
              history: [
                {
                  [selectedStrategy.currency.toLocaleLowerCase() as
                    | 'eur'
                    | 'usd']: lastPrice,
                },
              ],
            },
            entryPrice,
            unit,
            name,
          }) => {
            let treeMapValue = computePercentage(
              strategyValues.variation,
              unit * Math.abs(lastPrice - entryPrice)
            );
            treeMapValue < 3 && (treeMapValue = 3);

            return {
              name: name.split('-')[0].slice(0, treeMapValue === 3 ? 8 : 15),
              tooltipName: name.split('-')[0],
              percentage: computePercentage(entryPrice, lastPrice, 100),
              value: unit * (lastPrice - entryPrice),
              currency: selectedStrategy.currency,
              treeMapValue,
            };
          }
        )
      : aggregateGroups({
          assets: selectedStrategy.strategyAssets.items,
          currency: selectedStrategy.currency,
          groupBy:
            selectedFilter === 'Industry'
              ? 'INDUSTRY'
              : selectedFilter === 'Country'
              ? 'COUNTRY'
              : selectedFilter === 'Currency'
              ? 'CURRENCY'
              : 'STRATEGIES',
        }).map(
          ({
            title: name,
            entryValue: entryGroupValue,
            totalValue: lastGroupValue,
          }) => {
            let treeMapValue = computePercentage(
              strategyValues.variation,
              Math.abs(lastGroupValue - entryGroupValue)
            );
            treeMapValue < 3 && (treeMapValue = 3);

            return {
              currency: selectedStrategy.currency,
              name,
              percentage: computePercentage(
                entryGroupValue,
                lastGroupValue,
                100
              ),
              tooltipName: name,
              treeMapValue,
              value: lastGroupValue - entryGroupValue,
            };
          }
        )
  ).sort(
    (a, b) =>
      b.treeMapValue - a.treeMapValue || Math.abs(b.value) - Math.abs(a.value)
  );

  return (
    <div className="w-full h-full">
      {!strategyId && (
        <div className="flex justify-between">
          <Tabs
            selectedStrategy={selectedStrategy}
            selectedStrategies={selectedStrategies}
            collapsed={width === 1}
            onTabChange={handleStrategySelect}
            restoreTab={handleStrategyRemoval}
            multiple={false}
          />
        </div>
      )}

      <div
        className={`flex w-full
          ${strategyId ? 'h-full' : 'h-full-18'}
          ${width === 1 ? 'flex-col gap-2' : ''}
        `}
      >
        <div
          className={`${
            width === 3 ? 'w-2/12' : width === 2 ? 'w-3/12' : 'w-full'
          }`}
        >
          <div
            className={` border border-grey-10  mx-4 rounded-lg ${
              strategyId ? 'mt-4 p-4 mb-2' : 'mt-0 p-3 mb-0'
            }`}
          >
            {t('widget.contribution.variation')}
            <ProfitLoss
              value={strategyValues.variation}
              prefixValue={
                strategyValues.initialValue < strategyValues.currentValue
                  ? '+'
                  : ''
              }
              displayType="text"
              symbol={
                selectedStrategy.currency
                  ? getSymbol(selectedStrategy.currency)
                  : undefined
              }
              isRow={!strategyId}
              valueClassName={'mr-2'}
              percentage={strategyValues.variationPercentage}
            />
          </div>

          <ContributionWidgetFilters
            filtersList={filtersList}
            small={width === 1}
            onChange={setSelectedFilter}
          />
        </div>

        <div
          className={`${
            width === 3 ? 'w-10/12' : width === 2 ? 'w-9/12' : 'w-full'
          }`}
        >
          <TreemapChart data={chartData as any} />
        </div>
      </div>
    </div>
  );
};

export default Contribution;
